import { recordInputDomain } from './recordInputSchema.js';

/** Visibility and chronological ordering for learner records in every Domain. */
export function getDomainRecordOrderField(domain) {
  // A null binding preserves mixed historical timestamp shapes. It must not
  // be replaced by a Firestore orderBy, which excludes missing fields.
  return Object.prototype.hasOwnProperty.call(domain?.runtime || {}, 'eventOrderField')
    ? domain.runtime.eventOrderField : 'createdAt';
}

/** A saved answer never becomes a lifecycle flag merely because of its name. */
export function isCurrentDomainRecord(record, domain = null) {
  if (!record) return false;
  domain = recordInputDomain(record, domain);
  // These are reserved event metadata, independent of the configured answers.
  if (record.voidedAt || record.deletedAt != null || record._draft === true
    || ['draft', 'voided', 'deleted', 'cancelled'].includes(String(record._status || '').toLowerCase())) return false;
  // A form answer called "status" or "active" is not record lifecycle metadata.
  const answerIds = new Set([domain?.primary, ...(domain?.fields || []), ...(domain?.followUp || [])]
    .filter(Boolean).map(field => field.id));
  if ((!answerIds.has('active') && record.active === false)
    || ['voided', 'deleted', 'isDeleted'].some(key => !answerIds.has(key) && record[key] === true)) return false;
  const status = String(record.status || '').toLowerCase();
  const state = String(record.state || '').toLowerCase();
  const adjudication = String(record.adjudicationStatus || '').toLowerCase();
  if ((!answerIds.has('status') && ['draft', 'voided', 'deleted', 'cancelled'].includes(status))
    || (!answerIds.has('state') && ['draft', 'voided', 'deleted', 'cancelled'].includes(state))
    || (!answerIds.has('adjudicationStatus') && adjudication === 'voided')) return false;
  return true;
}

export function isVisibleDomainRecord(record, domainOrId = null) {
  const domain = typeof domainOrId === 'object' ? domainOrId : null;
  const domainId = domain?.id || (typeof domainOrId === 'string' ? domainOrId : null);
  if ((!record?.studentId && !record?.learnerId) || !isCurrentDomainRecord(record, domain)) return false;
  return !domainId || !record.domainId || record.domainId === domainId;
}

export function domainRecordMillis(record) {
  const value = record?.issuedAt || record?.createdAt || record?.timestamp;
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function sortDomainRecords(records) {
  return [...records].sort((left, right) => domainRecordMillis(right) - domainRecordMillis(left));
}
